/**
 * Public Image Converter API Client
 *
 * Handles communication with /api/public/convert endpoint.
 * No authentication required.
 */

import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from '@/config';

export type ImageFormat = 'png' | 'jpeg' | 'jpg' | 'webp' | 'avif' | 'bmp' | 'gif';

export interface FileWithMetadata {
  file: File;
  width: number;
  height: number;
  size: number; // bytes
}

export interface ConvertFileInput {
  data: string; // Base64-encoded image
  filename: string;
}

export interface ConvertRequest {
  files: ConvertFileInput[];
  target_format: ImageFormat;
  quality?: number; // 0-100, optional
}

export interface ConvertResultSuccess {
  success: true;
  filename: string;
  data: string; // Base64-encoded converted image
  format: string;
  original_size: number;
  converted_size: number;
  width?: number; // Image dimensions (optional for backwards compatibility)
  height?: number;
}

export interface ConvertResultError {
  success: false;
  filename: string;
  error: string;
}

export type ConvertResult = ConvertResultSuccess | ConvertResultError;

export interface ConvertResponse {
  results: ConvertResult[];
}

export interface RateLimitInfo {
  limit: number;
  remaining: number;
  reset: number; // Unix timestamp
  retryAfter?: number; // Seconds until retry allowed
}

export interface ConvertError {
  message: string;
  statusCode: number;
  rateLimitInfo?: RateLimitInfo;
}

/**
 * Converts files to Base64 format.
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Extracts rate limit info from response headers.
 */
function extractRateLimitInfo(headers: Record<string, string>): RateLimitInfo | undefined {
  const limit = headers['x-ratelimit-limit'];
  const remaining = headers['x-ratelimit-remaining'];
  const reset = headers['x-ratelimit-reset'];
  const retryAfter = headers['retry-after'];

  if (limit && remaining && reset) {
    return {
      limit: parseInt(limit, 10),
      remaining: parseInt(remaining, 10),
      reset: parseInt(reset, 10),
      retryAfter: retryAfter ? parseInt(retryAfter, 10) : undefined
    };
  }

  return undefined;
}

/**
 * Converts images using the public API.
 *
 * @throws ConvertError if conversion fails
 */
export async function convertImages(
  files: File[],
  targetFormat: ImageFormat,
  quality?: number
): Promise<ConvertResponse> {
  try {
    // Validate file count (max 3 for public API)
    if (files.length === 0) {
      throw new Error('No files provided');
    }
    if (files.length > 3) {
      throw new Error('Maximum 3 files allowed');
    }

    // Convert files to Base64
    const convertedFiles: ConvertFileInput[] = await Promise.all(
      files.map(async (file) => ({
        data: await fileToBase64(file),
        filename: file.name
      }))
    );

    // Make API request
    const response = await axios.post<ConvertResponse>(
      `${API_BASE_URL}/api/public/convert`,
      {
        files: convertedFiles,
        target_format: targetFormat,
        quality
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ error: string }>;

      // Extract rate limit info if available
      const rateLimitInfo = axiosError.response?.headers
        ? extractRateLimitInfo(axiosError.response.headers as Record<string, string>)
        : undefined;

      const convertError: ConvertError = {
        message: axiosError.response?.data?.error || axiosError.message,
        statusCode: axiosError.response?.status || 500,
        rateLimitInfo
      };

      throw convertError;
    }

    // Unknown error
    throw {
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      statusCode: 500
    } as ConvertError;
  }
}

/**
 * Downloads a Base64-encoded image as a file.
 */
export function downloadBase64Image(base64Data: string, filename: string, format: string): void {
  // Create data URL
  const mimeType = `image/${format === 'jpg' ? 'jpeg' : format}`;
  const dataUrl = `data:${mimeType};base64,${base64Data}`;

  // Create temporary link and trigger download
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Formats file size in human-readable format.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

/**
 * Calculates compression percentage.
 */
export function calculateCompressionPercentage(originalSize: number, convertedSize: number): number {
  if (originalSize === 0) return 0;
  return Math.round(((originalSize - convertedSize) / originalSize) * 100);
}

/**
 * Extracts image dimensions from a File object.
 */
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
